import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";

const POLL_MS = 2000;
const MAX_ATTEMPTS = 10;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const kind = params.get("kind") || "booking";
  const [state, setState] = useState({ phase: "checking", data: null, error: null });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null, error: "Missing session id" });
      return;
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data, error: null });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({
            phase: "timeout",
            data,
            error: "Timed out checking status. Check your email — we'll confirm shortly.",
          });
          return;
        }
        timerRef.current = setTimeout(poll, POLL_MS);
      } catch (e) {
        setState({
          phase: "error",
          data: null,
          error: e?.response?.data?.detail || "Could not verify payment.",
        });
      }
    };

    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="bg-[#050505] text-white min-h-screen">
      <Navbar />
      <main className="pt-32 pb-20 max-w-3xl mx-auto px-4">
        <div className="bg-[#070B14] border border-white/5 p-10 md:p-14 text-center">
          {state.phase === "checking" && (
            <>
              <Loader2 size={48} className="text-[#60A5FA] mx-auto animate-spin" />
              <h1 className="font-display mt-6 text-3xl md:text-4xl font-bold uppercase tracking-tight">
                Verifying Payment
              </h1>
              <p className="mt-4 text-[#A1A1AA]">
                Hang tight — confirming with Stripe…
              </p>
            </>
          )}

          {state.phase === "paid" && (
            <>
              <CheckCircle2 size={56} className="text-[#2563EB] mx-auto" />
              <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
                [ Payment Confirmed ]
              </div>
              <h1 className="font-display mt-3 text-3xl md:text-4xl font-bold uppercase tracking-tight">
                {kind === "pack" ? "Pack Activated" : "Session Booked"}
              </h1>
              <p className="mt-4 text-[#A1A1AA]">
                {kind === "pack"
                  ? "Your 4-session pack is loaded. We sent a confirmation email — book individual sessions any time."
                  : "Your training slot is locked in. We sent a confirmation email with all the details."}
              </p>
              <div className="mt-8 bg-[#050505] border border-white/5 p-6 text-left max-w-md mx-auto">
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                  Receipt
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="text-white/70">Amount</span>
                  <span className="text-white font-semibold">
                    ${((state.data?.amount_total ?? 0) / 100).toFixed(2)}{" "}
                    {(state.data?.currency ?? "usd").toUpperCase()}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-white/70">Session</span>
                  <span className="text-white/90">
                    {state.data?.metadata?.program ||
                      state.data?.metadata?.pack_id ||
                      "—"}
                  </span>
                </div>
                {state.data?.metadata?.date && (
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-white/70">Date</span>
                    <span className="text-white/90">
                      {state.data.metadata.date} · {state.data.metadata.time_slot}
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/"
                  data-testid="success-home-btn"
                  className="border border-white/20 hover:border-[#2563EB] hover:text-[#60A5FA] px-6 py-3 text-sm font-bold tracking-widest uppercase"
                >
                  Back to Home
                </Link>
                {kind === "pack" ? (
                  <Link
                    to="/my-pack"
                    data-testid="success-my-pack-btn"
                    className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 text-sm font-bold tracking-widest uppercase"
                  >
                    Customer Portal
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/my-pack"
                      data-testid="success-portal-btn"
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 text-sm font-bold tracking-widest uppercase"
                    >
                      Customer Portal
                    </Link>
                    <Link
                      to="/book"
                      data-testid="success-book-another-btn"
                      className="border border-white/20 hover:border-[#2563EB] hover:text-[#60A5FA] px-6 py-3 text-sm font-bold tracking-widest uppercase"
                    >
                      Book Another
                    </Link>
                  </>
                )}
              </div>
            </>
          )}

          {state.phase === "expired" && (
            <>
              <AlertCircle size={48} className="text-[#60A5FA] mx-auto" />
              <h1 className="font-display mt-6 text-3xl font-bold uppercase tracking-tight">
                Session Expired
              </h1>
              <p className="mt-4 text-[#A1A1AA]">
                That Stripe checkout session expired. Pick a slot again to retry.
              </p>
              <Link
                to="/book"
                className="inline-block mt-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 text-sm font-bold tracking-widest uppercase"
              >
                Try Again
              </Link>
            </>
          )}

          {(state.phase === "timeout" || state.phase === "error") && (
            <>
              <AlertCircle size={48} className="text-[#60A5FA] mx-auto" />
              <h1 className="font-display mt-6 text-3xl font-bold uppercase tracking-tight">
                Hang Tight
              </h1>
              <p className="mt-4 text-[#A1A1AA]">
                {state.error || "Could not verify payment in time."}
              </p>
              <Link
                to="/"
                className="inline-block mt-8 border border-white/20 hover:border-[#2563EB] px-6 py-3 text-sm font-bold tracking-widest uppercase"
              >
                Back to Home
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
